import { useMemo } from 'react';
import { Box, Skeleton } from '@mantine/core';
import { useCommercialProposalsBlocks } from '@/models/commercial-proposal';
import {
  BlockTypes,
  getServiceBlockTiers,
  IServiceBlock,
  IServiceBlockConfig,
  IServiceBlockHeadingCell,
  isPackageBlock,
} from '@/models/commercial-proposals/blocks.store';
import { IPriceCellConfig } from '@/pages/commercial-proposals/blocks/cells/ServicePriceCell/ServicePriceCellSettings';
import ServiceMatrixTable, { fmtPrice, MatrixRow } from './ServiceMatrixTable';

const SERVICE_BLOCK_TYPES = [
  BlockTypes.Services,
  BlockTypes.ServicesInOnePackage,
  BlockTypes.ServicesInTwoPackages,
  BlockTypes.ServicesInThreePackages,
];

type THeadedConfig = IServiceBlockConfig & {
  heading?: IServiceBlockHeadingCell[];
};

function BlockTable({
  block,
  fallbackCurrency,
}: {
  block: IServiceBlock;
  fallbackCurrency: string;
}) {
  const tiers = getServiceBlockTiers(block.type);
  const isPackage = isPackageBlock(block.type);
  const heading = (block.config as THeadedConfig).heading || [];
  const rows = useMemo(
    () => block.services.filter((s) => !s.parentId && !s.config.hidden),
    [block],
  );
  // Names arrive on the block itself. This used to fan out one `service/details`
  // request per row purely to resolve them — 34 requests on a two-proposal offer,
  // which the browser's ~6-connection cap turned into ~2s of serial waves for 19KB.
  if (rows.length === 0) return null;

  const tierNames = isPackage
    ? Array.from(
        { length: tiers },
        (_, tier) => heading[tier + 1]?.title || `Package ${tier + 1}`,
      )
    : [];

  const matrixRows: MatrixRow[] = rows.map((svc) => {
    const currency = svc.price?.currency || fallbackCurrency;
    return {
      key: `${svc.serviceId}:${svc.weight}`,
      name: svc.record?.name || `Service #${svc.serviceId}`,
      price: fmtPrice(svc.price?.amount, currency),
      tiers: isPackage
        ? Array.from({ length: tiers }, (_, tier) => {
            const config = svc.config[tier] as IPriceCellConfig | undefined;
            const included = config?.included ?? false;
            const displayIcon = config?.displayIcon ?? true;
            return {
              included,
              label: displayIcon
                ? undefined
                : included
                  ? fmtPrice(svc.price?.amount ?? 0, currency) || ''
                  : `0 ${currency}`,
            };
          })
        : undefined,
    };
  });

  // Same rule the customer's page and the PDF price a package by.
  const totals = (block.config as { showSummaryRow?: boolean }).showSummaryRow
    ? tierNames.length === 0
      ? [
          fmtPrice(
            rows.reduce((t, svc) => t + Number(svc.price?.amount || 0), 0),
            rows[0]?.price?.currency || fallbackCurrency,
          ),
        ]
      : tierNames.map((_, tier) =>
          fmtPrice(
            rows.reduce(
              (t, svc) =>
                (svc.config[tier] as IPriceCellConfig | undefined)?.included
                  ? t + Number(svc.price?.amount || 0)
                  : t,
              0,
            ),
            rows[0]?.price?.currency || fallbackCurrency,
          ),
        )
    : null;

  return (
    <ServiceMatrixTable
      title={block.title}
      tierNames={tierNames}
      rows={matrixRows}
      totals={totals}
    />
  );
}

export default function ProposalBlocksPreview({
  cpId,
  currency,
}: {
  cpId: number;
  currency?: string | null;
}) {
  const { data: blocks, isLoading } = useCommercialProposalsBlocks(cpId);

  const serviceBlocks = (blocks || []).filter(
    (b): b is IServiceBlock =>
      SERVICE_BLOCK_TYPES.includes(b.type) &&
      ((b as IServiceBlock).services?.length ?? 0) > 0,
  );

  if (isLoading) return <Skeleton height={60} mt={4} />;
  if (serviceBlocks.length === 0) return null;

  return (
    <Box pl="sm" style={{ overflowX: 'auto' }}>
      {serviceBlocks.map((block) => (
        <BlockTable
          key={block.uuid}
          block={block}
          fallbackCurrency={currency || 'EUR'}
        />
      ))}
    </Box>
  );
}
